import React from 'react';
import { motion } from 'framer-motion';
type TimeBasedItem = {
  label: string;
  interval: string;
  note?: string;
};
export function TrustMoment() {
  const milestones = [{
    mileage: 'Every 5,000',
    unit: 'Miles',
    items: 'Oil and filter service. Fluid level check. Tire and brake inspection. The baseline that keeps everything else in context.'
  }, {
    mileage: '30,000',
    unit: 'Miles',
    items: "Air and cabin filter replacement. Fluid services — driveline fluid drain and fills. Battery inspection. Minnesota's climate is hard on batteries — most last 3 to 5 years here before they need replacement."
  }, {
    mileage: '60,000',
    unit: 'Miles',
    items: 'Services from 30K will be due again. Carbon removal procedure. Some vehicles will be due for spark plugs at this interval.'
  }, {
    mileage: '100,000',
    unit: 'Miles',
    items: 'Water pump, timing belt, spark plugs, coolant flush, plus any service due again on the 30K-60K interval. If you have maintained your vehicle to this point, you’re ahead of most people – and these services keep you there.'
  }];
  const timeBasedItems: TimeBasedItem[] = [{
    label: 'Brake fluid',
    interval: '2–3 years'
  }, {
    label: 'Tires',
    interval: '10 years',
    note: 'Regardless of tread depth'
  }, {
    label: 'Battery',
    interval: '3–5 years'
  }, {
    label: 'AGM battery',
    interval: '4–6 years'
  }, {
    label: 'Timing belt',
    interval: '7–10 years',
    note: 'If the mileage interval does not come first'
  }, {
    label: 'Serpentine belt',
    interval: '6–8 years'
  }, {
    label: 'TPMS sensors',
    interval: '7–10 years'
  }, {
    label: 'Key fob battery',
    interval: '1–2 years'
  }];
  return <section className="bg-brand-dark px-4 py-20 text-white" data-id="element-218" data-mp="3klr9evsoa16">
      <div className="mx-auto max-w-6xl" data-id="element-219" data-mp="3n7y1cav2lic">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="mx-auto mb-16 max-w-3xl text-center" data-id="element-220" data-mp="1zni32uved6wb">
          <span className="mb-4 block text-sm font-bold uppercase tracking-widest text-brand-accent" data-id="element-221" data-mp="1gz3d8awtfyx3">
            YOUR VEHICLE&apos;S MAINTENANCE SCHEDULE
          </span>
          <h2 className="mb-6 font-heading text-3xl font-extrabold uppercase tracking-wide md:text-4xl lg:text-5xl" data-id="element-222" data-mp="1qnl9qfy2bjux">
            DIFFERENT MILEAGES. DIFFERENT NEEDS. HERE&apos;S WHAT TO EXPECT.
          </h2>
          <p className="text-lg leading-relaxed text-gray-300 md:text-xl" data-id="element-223" data-mp="y70r4k1enqfg">
            Every vehicle has a manufacturer-recommended maintenance schedule.
            Most people have never read it. Here&apos;s what typically comes due
            at each major milestone — and why it matters.
          </p>
        </motion.div>

        <div className="relative mb-10" data-id="element-224" data-mp="39nb38ft2ind6">
          <div className="absolute left-0 right-0 top-6 hidden h-0.5 bg-brand-accent/40 lg:block" aria-hidden="true" data-id="element-225" data-mp="39odu4nqe4pt3" />
          <ol className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 lg:gap-6" data-id="element-226" data-mp="2zxrfavqi03bg">
            {milestones.map((milestone, index) => <motion.li key={milestone.mileage} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.08
          }} className="relative flex flex-col" data-id="element-227" data-mp="3nkhk24u8ebzr" data-mpi={index}>
                <div className="mb-4 flex justify-center lg:justify-start" data-id="element-228" data-mp="2nfgqdl4u48w5" />
                <div className="flex-grow rounded-2xl border border-gray-200 bg-brand-light p-6 text-center shadow-sm lg:text-left" data-id="element-229" data-mp="2iogr4988teqh">
                  <p className="font-heading text-2xl font-extrabold uppercase leading-none tracking-wide text-brand-accent" data-id="element-230" data-mp="2dqf9ztudnphn">
                    {milestone.mileage}
                  </p>
                  <p className="mb-4 font-heading text-xs font-bold uppercase tracking-widest text-gray-500" data-id="element-231" data-mp="36eb54u4at9cb">
                    {milestone.unit}
                  </p>
                  <p className="text-base leading-relaxed text-gray-700" data-id="element-232" data-mp="2xdczk7e7otrm">
                    {milestone.items}
                  </p>
                </div>
              </motion.li>)}
          </ol>
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="mx-auto mb-10 max-w-3xl text-center" data-id="element-233" data-mp="1lyr3vp26zz79">
          <p className="text-sm leading-relaxed text-gray-400 md:text-base" data-id="element-234" data-mp="1e2qfptud0z2">
            These are general guidelines. Exact intervals vary by make, model,
            and driving conditions — and some vehicles use timing chains that
            don't need replacement. We'll tell you what's actually due for your
            vehicle, and be honest about what's fine.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="text-center" data-id="element-235" data-mp="k8rgl6cxp6u6">
          <a href="https://www.lancerservice.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-md bg-brand-accent px-8 py-4 font-bold text-white shadow-lg shadow-brand-accent/20 transition-colors hover:bg-brand-accentHover" data-id="element-236" data-mp="2uzhl7wd84oxr">
            Schedule a Maintenance Inspection →
          </a>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-16 rounded-2xl border-2 border-brand-accent bg-[#C8B4A4] p-6 shadow-xl shadow-black/30 md:p-8" data-id="element-237" data-mp="75f4oeq8rq0o">
          <div className="mb-6 border-b border-brand-dark/15 pb-5 text-center sm:text-left" data-id="element-238" data-mp="13tbortkw13gn">
            <p className="font-heading text-2xl font-extrabold uppercase leading-none tracking-wide text-brand-dark md:text-3xl" data-id="element-239" data-mp="yui6zoykiqao">
              Time-Based Maintenance
            </p>
          </div>
          <ul className="grid grid-cols-1 gap-4 text-left sm:grid-cols-2 lg:grid-cols-4" data-id="element-240" data-mp="1nfd5o0n8rvi8">
            {timeBasedItems.map(item => <li key={item.label} className="flex h-full flex-col rounded-xl border border-brand-dark/10 bg-white/70 p-4 shadow-sm transition-colors hover:border-brand-accent/60 hover:bg-white" data-id="element-241" data-mp="19ekd3lgi3ahr">
                <span className="font-heading text-xs font-bold uppercase tracking-widest text-brand-accentHover" data-id="element-242" data-mp="znzfo1ckcc0f">
                  {item.label}
                </span>
                <span className="mt-1 font-heading text-xl font-extrabold leading-none text-brand-dark" data-id="element-243" data-mp="pt7igymw7r66">
                  {item.interval}
                </span>
                {item.note && <span className="mt-2 text-sm leading-snug text-brand-dark/70" data-id="element-244" data-mp="gs9ay6d8x1iu">
                    {item.note}
                  </span>}
              </li>)}
          </ul>
        </motion.div>
      </div>
    </section>;
}